/*
 * Cristaux — open the desktop mega menu on hover instead of click.
 *
 * Dawn ships the mega menu as <header-menu><details><summary>…, so it opens on
 * click and Dawn's HeaderMenu class (assets/details-disclosure.js) only reacts
 * to the resulting `toggle` event. Rather than patch that core class, this file
 * layers hover intent on top of the same <details> element: opening is still
 * just `open` being set, so every Dawn transition, the caret rotation and the
 * `--header-bottom-position-desktop` bookkeeping keep working untouched.
 *
 * Loaded from sections/header.liquid only when the desktop menu type is "mega"
 * AND the "Open mega menu on hover" setting is on.
 *
 * Accessibility notes:
 * - Hover only applies on real hover-capable pointers (`hover: hover` +
 *   `pointer: fine`). Touch and stylus fall through to Dawn's click behaviour.
 * - Keyboard is untouched: a keyboard-activated click reports `detail === 0`,
 *   so Enter/Space still toggles the panel. Only genuine mouse clicks are
 *   swallowed, because hover already owns the open state and the click would
 *   otherwise immediately close what the mouse just opened.
 * - Escape closes the open panel and returns focus to its summary.
 */

(function () {
  /* Grace period on leave, so diagonal mouse travel from the summary into the
     panel (or between panels) doesn't flicker the menu shut. */
  const CLOSE_DELAY = 150;

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function menus(scope) {
    return (scope || document).querySelectorAll('.header__inline-menu header-menu');
  }

  function openMenu(menu) {
    const details = menu.querySelector('details');
    if (!details || details.hasAttribute('open')) return;

    const summary = details.querySelector('summary');
    const content = details.querySelector('.mega-menu__content');
    const headerWrapper = menu.closest('.header-wrapper');
    if (summary && content && headerWrapper) {
      const top = summary.getBoundingClientRect().bottom - headerWrapper.getBoundingClientRect().top;
      content.style.setProperty('--mega-top', `${top}px`);
    }

    details.setAttribute('open', '');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  function closeMenu(menu) {
    const details = menu.querySelector('details');
    if (!details || !details.hasAttribute('open')) return;

    details.removeAttribute('open');
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  function closeOthers(current) {
    menus().forEach((menu) => {
      if (menu !== current) closeMenu(menu);
    });
  }

  function bind(menu) {
    if (menu.dataset.cristauxHover === 'true') return;
    menu.dataset.cristauxHover = 'true';

    let closeTimer;
    const cancelClose = () => {
      clearTimeout(closeTimer);
      closeTimer = undefined;
    };

    /* pointerenter/pointerleave (not …over/…out) don't fire when the pointer
       moves between descendants, and the panel is a descendant of <header-menu>
       even though it's positioned outside the summary's box — so moving down
       into the panel never counts as leaving. */
    menu.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !canHover.matches) return;

      cancelClose();
      closeOthers(menu);
      openMenu(menu);
    });

    menu.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || !canHover.matches) return;

      cancelClose();
      closeTimer = setTimeout(() => closeMenu(menu), CLOSE_DELAY);
    });

    const summary = menu.querySelector('summary');
    if (!summary) return;

    summary.addEventListener('click', (event) => {
      /* detail === 0 means keyboard activation — let Dawn toggle normally. */
      if (!canHover.matches || event.detail === 0) return;

      event.preventDefault();
      cancelClose();
      closeOthers(menu);
      openMenu(menu);
    });

    summary.addEventListener('focus', () => {
      cancelClose();
    });

    menu.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape') return;

      cancelClose();
      closeMenu(menu);
      summary.focus();
    });
  }

  function init(scope) {
    menus(scope).forEach(bind);
  }

  init();

  /* The theme editor re-renders the header section on every settings change. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
